import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import "./sccs/FormInputTable.scss";
import { get } from 'lodash';
import Delete from '../../../../img/icons/svg/botones/delete.png';
import { DatePickerComponent, ChangeEventArgs } from '@syncfusion/ej2-react-calendars';

interface IInputProps {
    /**
      * Nombre que se aplicará en el ArrayFIeld.
      */
    name: string;
    /**
     * Nombre que se aplicará en la tabla.
     * 
     * Ejemplo: ['columna1', 'columna2', 'columna3']
     */
    heads: string[];
    /**
     * Nombre que se aplicará en el register del useForm.
     * 
     * Ejemplo: ['c1', 'c2', 'c3']
     */
    nameHeads: string[];
    removeButton?: boolean;
    addButton?: boolean;
    widthColumn?: number[];
    validateFunction?: (value: any[]) => true | string;
    /**
     * Define el tipo de input por columna.
     * - 1: Campo de texto
     * - 2: Campo enteros
     * - 3: Campo decimales (con . o ,)
     * - 4: Campo de fecha
     * - 5: Campo de Si o No
     * - 6: Discapacidad List
     * - 7: Campo de mes numerico (01-12)
     * - 8: Campo de dia numerico (01-31)
     * - 9: Campo de anio numerico (maximo 4 digitos)
     * - 10: Campo de edad (maximo 3 digitos enteros)
    * - 11: Campo de fecha sin fechas futuras
     * 
     * Ejemplo: [1, 2, 1] = primera columna texto, segunda número, tercera texto
     */
    inputType?: number[];
    arrayModal?: (string | null)[] | null;
    disabledFields?: (boolean | null)[] | [];
}

/**
 * Metodo que rellena la tabla de Forma segura.
 * - 1º Parametro: Los datos que se van a rellenar (se debe pasar con la estructura)
 * - 2º Parametro: La longitud que debe tener la tabla
 * - 3º Parametro: Estructura de las filas vacias que se van a rellenar en caso de que la tabla tenga menos filas que la longitud indicada
 *
 */
export const fillArray = (array: any[] = [], targetLength: number, emptyItem: object) => {
    const safeArray = Array.isArray(array) ? array : [];
    const normalizedArray = safeArray.map(item => normalizeValue(item));
    const filled = [...normalizedArray];
    const normalizedEmpty = normalizeValue(emptyItem);

    while (filled.length < targetLength) {
        filled.push({ ...normalizedEmpty });
    }

    return filled;
};

function normalizeValue(value: any): any {
    if (typeof value === "string") {
        // Detecta ISO: "2025-12-01T00:00:00"
        if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
            return value.split("T")[0];
        }
        return value;
    }

    if (Array.isArray(value)) {
        return value.map(v => normalizeValue(v));
    }

    if (value && typeof value === "object") {
        const normalized: any = {};
        for (const key in value) {
            normalized[key] = normalizeValue(value[key]);
        }
        return normalized;
    }

    return value;
}

const getNextInputValue = (input: HTMLInputElement, valueToInsert: string) => {
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? input.value.length;

    return `${input.value.slice(0, selectionStart)}${valueToInsert}${input.value.slice(selectionEnd)}`;
};

const isIntegerInputValue = (value: string) => /^-?[0-9]*$/.test(value);
const isDecimalInputValue = (value: string) => /^-?[0-9]*([.,][0-9]*)?$/.test(value);
const isTwoDigitInputValue = (value: string) => /^[0-9]{0,2}$/.test(value);
const isAgeInputValue = (value: string) => /^[0-9]{0,3}$/.test(value);

const formatTwoDigitValue = (value: any) => {
    if (value === undefined || value === null || value === "") return "";

    const valueAsString = String(value);
    if (/^[1-9]$/.test(valueAsString)) {
        return valueAsString.padStart(2, "0");
    }

    return valueAsString;
};

const parseIntegerOrNull = (value: any) => {
    if (value === undefined || value === null || value === "") return null;

    const valueAsString = String(value);
    return /^[0-9]+$/.test(valueAsString) ? parseInt(valueAsString, 10) : value;
};

const validateNumberBetween = (value: any, min: number, max: number, message: string) => {
    if (value === undefined || value === null || value === "") return true;

    const numericValue = Number(value);
    return Number.isInteger(numericValue) && numericValue >= min && numericValue <= max
        ? true
        : message;
};

const createDateFromYear = (year: number) => {
    const date = new Date(0);
    date.setFullYear(year, 0, 1);
    date.setHours(0, 0, 0, 0);
    return date;
};

const getCurrentYear = () => new Date().getFullYear();

const getYearNumber = (value: any): number | null => {
    if (value === undefined || value === null || value === "") return null;

    if (value instanceof Date && !isNaN(value.getTime())) {
        return value.getFullYear();
    }

    const valueAsString = String(value);
    if (/^[0-9]{1,4}$/.test(valueAsString)) {
        return parseInt(valueAsString, 10);
    }

    const parsedDate = new Date(value);
    return !isNaN(parsedDate.getTime()) ? parsedDate.getFullYear() : null;
};

const validateYearNotInFuture = (value: any) => {
    if (value === undefined || value === null || value === "") return true;

    const currentYear = getCurrentYear();
    const year = getYearNumber(value);

    return year !== null && year <= currentYear
        ? true
        : `El año no puede ser superior a ${currentYear}`;
};

const getDatePickerValue = (value: any): Date | null => {
    if (value === undefined || value === null || value === "") return null;

    if (value instanceof Date && !isNaN(value.getTime())) {
        return value;
    }

    if (typeof value === "string") {
        const normalizedValue = value.split("T")[0];
        const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizedValue);

        if (isoDateMatch) {
            return new Date(
                Number(isoDateMatch[1]),
                Number(isoDateMatch[2]) - 1,
                Number(isoDateMatch[3])
            );
        }
    }

    const parsedDate = new Date(value);
    return !isNaN(parsedDate.getTime()) ? parsedDate : null;
};

const getComparableDate = (value: any): Date | null => {
    const date = getDatePickerValue(value);
    if (!date) return null;

    const comparableDate = new Date(date);
    comparableDate.setHours(0, 0, 0, 0);
    return comparableDate;
};

const getTodayComparableDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

const getTodayPickerMaxDate = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
};

const validateDateNotInFuture = (value: any) => {
    if (value === undefined || value === null || value === "") return true;

    const selectedDate = getComparableDate(value);
    if (!selectedDate) return "Introduce una fecha válida";

    return selectedDate <= getTodayComparableDate()
        ? true
        : "La fecha no puede ser futura";
};

const getYearDateValue = (value: any): Date | null => {
    if (value === undefined || value === null || value === "") return null;

    if (value instanceof Date && !isNaN(value.getTime())) {
        return createDateFromYear(value.getFullYear());
    }

    const valueAsString = String(value);
    if (/^[0-9]{1,4}$/.test(valueAsString)) {
        return createDateFromYear(parseInt(valueAsString, 10));
    }

    const parsedDate = new Date(value);
    return !isNaN(parsedDate.getTime())
        ? createDateFromYear(parsedDate.getFullYear())
        : null;
};

const preventInvalidNumericInput = (
    event: React.FormEvent<HTMLInputElement>,
    isValidValue: (value: string) => boolean
) => {
    const nativeEvent = event.nativeEvent as InputEvent;
    const insertedValue = nativeEvent.data;

    if (!insertedValue) return;

    const nextValue = getNextInputValue(event.currentTarget, insertedValue);
    if (!isValidValue(nextValue)) {
        event.preventDefault();
    }
};

const preventInvalidNumericPaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    isValidValue: (value: string) => boolean
) => {
    const pastedValue = event.clipboardData.getData("text");
    const nextValue = getNextInputValue(event.currentTarget, pastedValue);

    if (!isValidValue(nextValue)) {
        event.preventDefault();
    }
};

const formatTwoDigitOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;

    if (/^[1-9]$/.test(value)) {
        event.currentTarget.value = value.padStart(2, "0");
    }
};

interface ITwoDigitInputProps {
    value: any;
    name: string;
    inputRef: React.Ref<HTMLInputElement>;
    disabled: boolean;
    onChange: (value: any) => void;
    onBlur: () => void;
}

const TwoDigitInput: React.FC<ITwoDigitInputProps> = ({
    value,
    name,
    inputRef,
    disabled,
    onChange,
    onBlur
}) => {
    const [displayValue, setDisplayValue] = useState(formatTwoDigitValue(value));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) {
            setDisplayValue(formatTwoDigitValue(value));
        }
    }, [focused, value]);

    return (
        <input
            type="text"
            name={name}
            ref={inputRef}
            disabled={disabled}
            value={displayValue}
            onBeforeInput={(event) => preventInvalidNumericInput(event, isTwoDigitInputValue)}
            onPaste={(event) => preventInvalidNumericPaste(event, isTwoDigitInputValue)}
            onFocus={() => setFocused(true)}
            onChange={(event) => {
                const nextValue = event.target.value;
                setDisplayValue(nextValue);
                onChange(parseIntegerOrNull(nextValue));
            }}
            onBlur={(event) => {
                setFocused(false);
                formatTwoDigitOnBlur(event);
                setDisplayValue(event.currentTarget.value);
                onChange(parseIntegerOrNull(event.currentTarget.value));
                onBlur();
            }}
            className="forminputable-td"
            style={{ width: '100%' }}
        />
    );
};


const FormInputTable: React.FC<IInputProps> = ({
    name,
    heads,
    nameHeads,
    removeButton = true,
    addButton = true,
    widthColumn,
    validateFunction,
    inputType = [],
    arrayModal = null,
    disabledFields = [],
}) => {
    const { register, control, formState: { errors } } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name,
        rules: validateFunction ? { validate: validateFunction } : undefined,
    });

    const [showModalIndex, setShowModalIndex] = useState<number | null>(null);

    const handleAddRow = () => {
        const newRow = nameHeads.reduce((acc, key) => {
            acc[key] = '';
            return acc;
        }, {} as Record<string, string>);
        append(newRow);
    };
    const isDisabled = (colIndex: number) => {
        return disabledFields?.[colIndex] === true;
    };

    const getWidth = (colIndex: number) => {
        const width = widthColumn?.[colIndex];
        if (width && width >= 1 && width <= 12) {
            return `${(width / 12) * 100}%`;
        }
        return `${100 / nameHeads.length}%`;
    };





    const errorMessage = get(errors, `${name}.root.message`);
    const [hoveredRow, setHoveredRow] = useState<number | null>(null);

    return (
        <div data-input-name={name} className='container col-12 align-items-center justify-content-center forminputtable-wrapper'>
            <table className="forminputtable">
                <thead className="forminputtable-head">
                    <tr>
                        {heads.map((head, index) => (
                            <th key={index} style={{ width: getWidth(index) }}>
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span>{head}</span>

                                    {arrayModal?.[index] && (
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowModalIndex(index);
                                            }}
                                            className="
                                            rounded-circle
                                            p-1
                                            d-inline-flex
                                            justify-content-center
                                            align-items-center
                                            position-relative
                                            accordion-modal
                                            mr-2
                                            "
                                        >
                                            <div className="information-icon-orange p-0" />
                                        </div>
                                    )}
                                </div>
                            </th>
                        ))}

                    </tr>
                </thead>
                <tbody>
                    {fields.map((field, rowIndex) => (
                        <tr key={field.id} className={hoveredRow === rowIndex ? "row-temptation-delete" : ""}>
                            {nameHeads.map((fieldKey, colIndex) => {
                                const type = inputType[colIndex];
                                const inputName = `${name}.${rowIndex}.${fieldKey}`;
                                const fieldError = get(errors, inputName);

                                return (
                                    <td className='' key={colIndex} style={{ width: getWidth(colIndex) }}>
                                        {(() => {
                                            switch (type) {
                                                case 10: { // EDAD
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <input
                                                                type="text"
                                                                inputMode="numeric"
                                                                maxLength={3}
                                                                onBeforeInput={(event) => preventInvalidNumericInput(event, isAgeInputValue)}
                                                                onPaste={(event) => preventInvalidNumericPaste(event, isAgeInputValue)}
                                                                {...register(inputName, {
                                                                    setValueAs: (value) => {
                                                                        const valueAsString = typeof value === "string" ? value : String(value ?? "");
                                                                        if (valueAsString === "") return null;

                                                                        return /^[0-9]{1,3}$/.test(valueAsString)
                                                                            ? parseInt(valueAsString, 10)
                                                                            : value;
                                                                    },
                                                                    validate: (value) => {
                                                                        if (value === undefined || value === null || value === "") return true;

                                                                        return /^[0-9]{1,3}$/.test(String(value))
                                                                            ? true
                                                                            : `Solo se admiten hasta 3 numeros enteros en ${heads[colIndex]}`;
                                                                    },
                                                                })}
                                                                disabled={isDisabled(colIndex)}
                                                                className="forminputable-td"
                                                                style={{ width: '100%' }}
                                                            />
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                case 9: { // ANIO NUMERICO
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <Controller
                                                                name={inputName}
                                                                control={control}
                                                                rules={{
                                                                    validate: validateYearNotInFuture,
                                                                }}
                                                                render={({ field }) => (
                                                                    <DatePickerComponent
                                                                        value={getYearDateValue(field.value)}
                                                                        max={createDateFromYear(getCurrentYear())}
                                                                        change={(args: ChangeEventArgs) => {
                                                                            const date = args.value as Date | null;
                                                                            field.onChange(date instanceof Date && !isNaN(date.getTime()) ? date.getFullYear() : null);
                                                                        }}
                                                                        format="yyyy"
                                                                        locale="es"
                                                                        firstDayOfWeek={1}
                                                                        start="Decade"
                                                                        depth="Decade"
                                                                        allowEdit={false}
                                                                        openOnFocus={true}
                                                                        disabled={isDisabled(colIndex)}
                                                                        placeholder="aaaa"
                                                                        cssClass="forminputable-td syncfusion-date-table"
                                                                    />
                                                                )}
                                                            />
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                case 8: { // DIA NUMERICO
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <Controller
                                                                name={inputName}
                                                                control={control}
                                                                rules={{
                                                                    validate: (value) => validateNumberBetween(value, 1, 31, "Introduce un dia valido entre 1 y 31"),
                                                                }}
                                                                render={({ field }) => (
                                                                    <TwoDigitInput
                                                                        value={field.value}
                                                                        name={field.name}
                                                                        inputRef={field.ref}
                                                                        disabled={isDisabled(colIndex)}
                                                                        onChange={field.onChange}
                                                                        onBlur={field.onBlur}
                                                                    />
                                                                )}
                                                            />
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                case 7: { // MES NUMERICO
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <Controller
                                                                name={inputName}
                                                                control={control}
                                                                rules={{
                                                                    validate: (value) => validateNumberBetween(value, 1, 12, "Introduce un mes valido entre 1 y 12"),
                                                                }}
                                                                render={({ field }) => (
                                                                    <TwoDigitInput
                                                                        value={field.value}
                                                                        name={field.name}
                                                                        inputRef={field.ref}
                                                                        disabled={isDisabled(colIndex)}
                                                                        onChange={field.onChange}
                                                                        onBlur={field.onBlur}
                                                                    />
                                                                )}
                                                            />
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                case 6: // DISCAPACIDAD LIST
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <select
                                                                {...register(inputName, {
                                                                    setValueAs: (v) => {
                                                                        if (v === "") return null;
                                                                        return v;
                                                                    },
                                                                })}
                                                                disabled={isDisabled(colIndex)}
                                                                className="forminputable-td"
                                                                style={{ width: '100%' }}
                                                                defaultValue=""
                                                            >
                                                                <option value="">Selecciona una opcion...</option>
                                                                <option value="Sin Discapacidad">Sin Discapacidad</option>
                                                                <option value="Superior o igual al 33% e Inferior al 65%">Superior o igual al 33% e Inferior al 65%</option>
                                                                <option value="Superior o igual al 65%">Superior o igual al 65%</option>
                                                            </select>
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>
                                                    )
                                                case 5: //YES OR NO
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <Controller
                                                                name={inputName}
                                                                control={control}
                                                                render={({ field }) => {
                                                                    const normalizedValue =
                                                                        field.value === true || field.value === "true" || field.value === 1 || field.value === "1"
                                                                            ? "true"
                                                                            : field.value === false || field.value === "false" || field.value === 0 || field.value === "0"
                                                                                ? "false"
                                                                                : "";

                                                                    return (
                                                                        <select
                                                                            name={field.name}
                                                                            ref={field.ref}
                                                                            value={normalizedValue}
                                                                            onBlur={field.onBlur}
                                                                            onChange={(event) => {
                                                                                const rawValue = event.target.value;
                                                                                if (rawValue === "") {
                                                                                    field.onChange(null);
                                                                                    return;
                                                                                }
                                                                                field.onChange(rawValue === "true");
                                                                            }}
                                                                            disabled={isDisabled(colIndex)}
                                                                            className="forminputable-td"
                                                                            style={{ width: '100%' }}
                                                                        >
                                                                            <option value="">Selecciona una opcion...</option>
                                                                            <option value="true">Sí</option>
                                                                            <option value="false">No</option>
                                                                        </select>
                                                                    );
                                                                }}
                                                            />
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>
                                                    )
                                                case 11: // FECHA SIN FUTURO
                                                case 4: // FECHA
                                                    return (
                                                        <div className={`${fieldError?.message ? 'error-td' : ''}`}>
                                                            <Controller
                                                                name={inputName}
                                                                control={control}
                                                                rules={type === 11 ? { validate: validateDateNotInFuture } : undefined}
                                                                render={({ field }) => (
                                                                    <DatePickerComponent
                                                                        value={getDatePickerValue(field.value)}
                                                                        change={(args) => {
                                                                            const date = args.value as Date | null;

                                                                            if (date) {
                                                                                date.setHours(12, 0, 0, 0); // evitar bugs de zona horaria
                                                                            }

                                                                            field.onChange(date);
                                                                        }}
                                                                        format="dd-MM-yyyy"
                                                                        locale="es"
                                                                        firstDayOfWeek={1}
                                                                        allowEdit={false}
                                                                        openOnFocus={true}
                                                                        max={type === 11 ? getTodayPickerMaxDate() : undefined}
                                                                        disabled={isDisabled(colIndex)}
                                                                        placeholder="dd-mm-aaaa"
                                                                        cssClass="forminputable-td syncfusion-date-table"
                                                                    />
                                                                )}
                                                            />

                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">
                                                                    {fieldError.message as string}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                case 3: //DECIMALES
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <input
                                                                type="text"
                                                                disabled={isDisabled(colIndex)}
                                                                onBeforeInput={(event) => preventInvalidNumericInput(event, isDecimalInputValue)}
                                                                onPaste={(event) => preventInvalidNumericPaste(event, isDecimalInputValue)}
                                                                {...register(inputName, {
                                                                    setValueAs: (v) => {
                                                                        const str = typeof v === "string" ? v : String(v ?? "");
                                                                        return /^-?[0-9]+([.,][0-9]+)?$/.test(str)
                                                                            ? parseFloat(str.replace(",", "."))
                                                                            : v;
                                                                    },
                                                                    pattern: {
                                                                        value: /^-?[0-9]+([.,][0-9]+)?$/,
                                                                        message: "Introduce un número válido (entero o decimal, con '.' o ',')"
                                                                    }
                                                                })}
                                                                className="forminputable-td"
                                                                style={{ width: '100%' }}
                                                            />
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>

                                                    )
                                                case 2: //ENTEROS
                                                    return (
                                                        <div className={` ${fieldError?.message ? 'error-td' : ''}`}>
                                                            <input
                                                                type="text"
                                                                onBeforeInput={(event) => preventInvalidNumericInput(event, isIntegerInputValue)}
                                                                onPaste={(event) => preventInvalidNumericPaste(event, isIntegerInputValue)}
                                                                {...register(inputName, {
                                                                    setValueAs: (v) => {
                                                                        if (v === "") return 0;
                                                                        return /^[0-9]+$/.test(v) ? parseInt(v, 10) : v; // solo parsea si son números
                                                                    },
                                                                    pattern: {
                                                                        value: /^[0-9]+$/,
                                                                        message: `Solo se admiten números enteros en ${heads[colIndex]}`,
                                                                    },
                                                                })}
                                                                disabled={isDisabled(colIndex)}
                                                                className="forminputable-td"
                                                                style={{ width: '100%' }}
                                                            />
                                                            {fieldError?.message && (
                                                                <p className="error-desplegable">{fieldError.message as string}</p>
                                                            )}
                                                        </div>
                                                    );
                                                case 1: //TEXTO LIBRE
                                                default:
                                                    return (
                                                        <input
                                                            type="text"
                                                            {...register(inputName)}
                                                            disabled={isDisabled(colIndex)}
                                                            className={`forminputable-td ${fieldError?.message ? 'error-td' : ''}`}
                                                            style={{ width: '100%' }}
                                                        />
                                                    );
                                            }
                                        })()}
                                    </td>
                                );
                            })}
                            {removeButton && (
                                <td className="text-center remove-button-table" style={{ width: "1%", whiteSpace: "nowrap" }}>
                                    <button
                                        style={{ padding: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}
                                        onClick={() => { remove(rowIndex); setHoveredRow(null) }}
                                        onMouseEnter={() => setHoveredRow(rowIndex)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        aria-label={`Eliminar fila ${rowIndex + 1}`}
                                    >
                                        <img src={Delete} alt="Eliminar" style={{ width: 18, height: 18 }} />
                                    </button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            {addButton && (
                <div className="row mb-2">
                    <div className="col-12">
                        <button className="btn-add-row" onClick={handleAddRow}>
                            +
                        </button>
                    </div>
                </div>

            )}

            {errorMessage && <p className="error m-0">{errorMessage as any}</p>}

            {/* MODAL */}
            <Modal show={showModalIndex !== null} onHide={() => setShowModalIndex(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Información</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showModalIndex !== null && arrayModal?.[showModalIndex] && (
                        <p>{arrayModal[showModalIndex]}</p>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default FormInputTable;
